
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

interface ProjectItem {
  id: number;
  titleJa: string;
  titleEn: string;
  category: string;
  image: string;
  link?: string;
  description: string;
}

const projects: ProjectItem[] = [
  {
    id: 1,
    titleJa: 'Echo — インタラクティブインスタレーション',
    titleEn: 'Interactive light & sound installation',
    category: 'Public Space',
    image: 'https://public.readdy.ai/ai/img_res/d1af1dec4dae59d855bfae9f2d457302.jpg',
    link: '/mirari-echo',
    description: 'DIG SHIBUYA 2025にて初公開。mirariとの共同制作による光と音のインタラクティブ体験。',
  },
  {
    id: 2,
    titleJa: 'MeVER Chair Pro v1.1',
    titleEn: 'Flagship ergonomic work chair',
    category: 'Private Space',
    image: 'https://readdy.ai/api/search-image?query=premium%20ergonomic%20office%20chair%20black%20mesh%20back%20modern%20minimalist%20design%20studio%20photography%20clean%20white%20background%20professional%20product%20shot%20high%20end%20furniture%20sleek%20contemporary%20style&width=800&height=500&seq=sw2&orientation=landscape',
    link: '/mever',
    description: 'BITMOVESが手がけるワーク＆ライフスタイルブランドのフラッグシップチェア。',
  },
  {
    id: 3,
    titleJa: 'DIG SHIBUYA 2025 会場施工',
    titleEn: 'Venue production & installation',
    category: 'Public Space',
    image: 'https://readdy.ai/api/search-image?query=modern%20event%20venue%20setup%20with%20dramatic%20lighting%20installation%20in%20urban%20space%20shibuya%20tokyo%20night%20event%20professional%20stage%20design%20ambient%20warm%20lighting%20contemporary%20architecture%20exhibition%20hall&width=800&height=500&seq=sw3&orientation=landscape',
    link: '/mirari-echo',
    description: '渋谷の都市空間を舞台にした大規模インスタレーションの施工・現場管理を担当。',
  },
  {
    id: 4,
    titleJa: 'MeVER Desk Core — 電動昇降デスク',
    titleEn: 'Electric standing desk',
    category: 'Private Space',
    image: 'https://readdy.ai/api/search-image?query=modern%20electric%20standing%20desk%20minimalist%20workspace%20setup%20clean%20wooden%20top%20black%20frame%20professional%20product%20photography%20bright%20studio%20lighting%20simple%20background%20contemporary%20office%20furniture&width=800&height=500&seq=sw4&orientation=landscape',
    link: '/mever',
    description: 'シンプルかつ堅牢な電動昇降デスク。ワークスペースの質を根本から変える。',
  },
  {
    id: 5,
    titleJa: '米軍基地向け調達・納品',
    titleEn: 'U.S. military base procurement & delivery',
    category: 'Global Standards',
    image: 'https://readdy.ai/api/search-image?query=professional%20logistics%20warehouse%20with%20organized%20furniture%20packages%20ready%20for%20delivery%20clean%20modern%20distribution%20center%20bright%20lighting%20industrial%20interior%20shipping%20containers%20organized%20storage&width=800&height=500&seq=sw5&orientation=landscape',
    description: '在日米軍基地への家具・什器の調達から納品までをワンストップで対応。',
  },
  {
    id: 6,
    titleJa: 'Atelier Folio for iPad',
    titleEn: 'iPad stand & folio case',
    category: 'Private Space',
    image: 'https://readdy.ai/api/search-image?query=premium%20iPad%20stand%20folio%20case%20elegant%20leather%20design%20minimalist%20desk%20accessory%20product%20photography%20clean%20background%20modern%20tech%20accessory%20professional%20studio%20shot%20warm%20neutral%20tones&width=800&height=500&seq=sw6&orientation=landscape',
    link: '/mever',
    description: '2026年3月Makuake公開予定。iPadをクリエイティブツールに変えるスタンド＆フォリオケース。',
  },
];

const clients = [
  { name: 'Mirari', alt: 'Mirari' },
  { name: 'MeVER', alt: 'MeVER' },
  { name: 'Steelcase', alt: 'Steelcase' },
  { name: 'Haworth', alt: 'Haworth' },
  { name: 'Herman Miller', alt: 'Herman Miller' },
  { name: 'Knoll', alt: 'Knoll' },
];

export default function SelectedWorks() {
  const { ref, isVisible } = useScrollAnimation();
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  return (
    <section
      ref={ref}
      id="selected-works"
      className="bg-white py-36 lg:py-44 px-8 lg:px-32"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div
          className={`mb-20 flex items-baseline gap-6 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="text-5xl lg:text-6xl font-medium text-gray-900">
            Selected Works
          </h2>
          <span className="text-base text-gray-500">実績</span>
        </div>

        {/* Project Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-28">
          {projects.map((project, index) => {
            const Wrapper = project.link ? Link : 'div';
            const wrapperProps = project.link
              ? { to: project.link }
              : {};

            return (
              <Wrapper
                key={project.id}
                {...(wrapperProps as any)}
                className={`group relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                }`}
                style={{
                  transitionDelay: isVisible ? `${index * 100 + 200}ms` : '0ms',
                }}
                onMouseEnter={() => setHoveredId(project.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                <div className="w-full h-72 lg:h-80 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.titleEn}
                    title={`${project.titleJa} - BITMOVES JAPAN`}
                    className={`w-full h-full object-cover object-top transition-transform duration-700 ${
                      hoveredId === project.id ? 'scale-110' : 'scale-100'
                    }`}
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <p className="text-xs uppercase tracking-wider text-white/60 mb-2">
                    {project.category}
                  </p>
                  <h3 className="text-lg font-medium text-white mb-1">
                    {project.titleJa}
                  </h3>
                  <p className="text-sm text-white/70 mb-3">
                    {project.titleEn}
                  </p>
                  <p
                    className={`text-sm text-white/80 leading-relaxed transition-all duration-500 ${
                      hoveredId === project.id
                        ? 'opacity-100 translate-y-0 max-h-20'
                        : 'opacity-0 translate-y-4 max-h-0'
                    } overflow-hidden`}
                  >
                    {project.description}
                  </p>
                </div>
                {project.link && (
                  <div
                    className={`absolute top-6 right-6 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm transition-all duration-300 ${
                      hoveredId === project.id ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
                    }`}
                  >
                    <i className="ri-arrow-right-up-line text-white text-lg w-5 h-5 flex items-center justify-center" />
                  </div>
                )}
              </Wrapper>
            );
          })}
        </div>

        {/* Clients & Partners */}
        <div
          className={`transition-all duration-700 delay-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h3 className="text-2xl font-medium text-gray-900 mb-12 text-center">
            Clients &amp; Partners
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 items-center">
            {clients.map((client) => (
              <div
                key={client.name}
                className="flex items-center justify-center p-8 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors duration-300"
              >
                <span className="text-xl font-medium text-gray-400 hover:text-gray-900 transition-colors duration-300 tracking-wider">
                  {client.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
